import {
  EventStatistics,
  EventStatisticsProgress,
  MissionJobRead,
  OdysseyEventStatistics,
  OdysseyRead,
  RobotRead,
  SchedulerSettings,
} from 'api/schema'
import { addHours, addSeconds } from 'date-fns'
import { t } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { PATH } from 'utils/constants'
import { VENDOR } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import schedulerStyle from './SchedulerSettings.module.css'

export interface ProcessedEvent {
  event_id: string | number
  title: string
  start: Date
  end: Date
  color?: string
  description?: string | React.ReactNode
  editable?: boolean
  deletable?: boolean
  draggable?: boolean
  allDay?: boolean
  duration?: number
  extendedProps?: any
}

/**
 * Generates a list of ProcessedEvents to occur within the query start and end,
 * based on the provided schedule.
 * @param start The start of the query, which is generally 00:00:00 of the first
 * day in the calendar view.
 * @param end The end of the query, which is generally 23:59:59 of the last day
 * in the calendar view.
 * @param task The current scheduled task, to be checked if there are any events
 * between start and end.
 * @param description Task description.
 * @param robot_name Robot name.
 * @returns List of ProcessedEvents to occur within the query start and end.
 */
export const scheduleToEvents = (
  start: Date,
  end: Date,
  task: MissionJobRead | OdysseyRead,
  getEventColor: () => string | undefined,
  description: string | React.ReactNode,
  robot: RobotRead,
  missionMap: Map<number, string>,
  facilityIds: number[] = []
): ProcessedEvent[] => {
  if (!task) {
    console.warn('Task is null!')
    return []
  }
  const robot_name = robot.serial_number || ''
  const robot_sn = robot.serial_number
  const robot_type = (robot as any).robot_type_display || robot.robot_type
  const vendor = (robot.vendor as VendorCode) || VENDOR.AUTONOMICS
  const task_type = 'Task'
  const id_prefix = 'mission_id' in task ? 'task_' : 'odyssey_'
  const mission_id = 'mission_id' in task ? task.mission_id : undefined
  const mission_name = 'mission_id' in task ? missionMap.get(task.mission_id) || task.name : task.name
  let eventDate: Date
  if (task.scheduler_settings && task.scheduler_settings.datetime) {
    eventDate = new Date(task.scheduler_settings.datetime.replace(' ', 'T'))
  } else {
    eventDate = new Date(task.created_at.replace(' ', 'T'))
    const validDateString = task.created_at.slice(0, 16) // Remove extra info
    eventDate = new Date(validDateString)
  }

  if ((!task.scheduler_settings?.weekdays && eventDate < start) || eventDate > end) {
    return []
  }
  const events: ProcessedEvent[] = []

  // Calculate duration in seconds
  let duration = 3 * 3600 // Default to 3 hours
  if ('execution_time' in task && task.execution_time) {
    duration = task.execution_time
  }

  const dayDuration = 24 * 60 * 60 * 1000
  const weekDuration = 7 * dayDuration
  const taskName = 'Robot ' + robot_name + ' | ' + task_type + ' ' + task?.id + ' | ' + task?.name
  if (task.scheduler_settings?.weekdays) {
    let dayToStart = new Date(Math.max(start.getTime(), eventDate.getTime()))
    const daysOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as (keyof SchedulerSettings['weekdays'])[]
    let counter = 0
    for (const dayOfWeek of daysOfWeek) {
      if (task.scheduler_settings.weekdays[dayOfWeek] === 'true') {
        let dayOffset = daysOfWeek.indexOf(dayOfWeek) - dayToStart.getDay()
        // Ensure dayOffset is always positive
        if (dayOffset < 0) {
          dayOffset += 7
        }
        let startDate = new Date(dayToStart.valueOf() + dayOffset * dayDuration)
        startDate.setHours(eventDate.getHours(), eventDate.getMinutes())
        let displayedDescription = description
        const { events: newEvents, counter: nextCounter } = addEvents(
          startDate,
          end,
          weekDuration,
          id_prefix + task.id,
          taskName,
          getEventColor,
          duration,
          task.scheduler_settings.except_days,
          displayedDescription,
          counter,
          {
            task_type,
            task_id: task.id,
            missions: 'missions' in task ? task.missions : undefined,
            missions_info: 'missions_info' in task ? task.missions_info : undefined,
            scenario: 'scenario' in task ? task.scenario : undefined,
            robot_type,
            robot_name,
            robot_sn,
            robot_id: robot.id,
            mission_name: mission_name || '',
            mission_id: mission_id,
            vendor: vendor,
            facility_ids: facilityIds,
          },
          task.scheduler_settings.except_days_descriptions,
          task.scheduler_settings.except_days_users
        )
        events.push(...newEvents)
        counter = nextCounter
      }
    }
  } else if (eventDate >= start && eventDate <= end) {
    const newEvent: ProcessedEvent = {
      start: eventDate,
      end: addSeconds(eventDate, duration),
      event_id: id_prefix + task.id + '_0',
      title: taskName,
      color: getEventColor(),
      duration: duration,
      description: description,
    }
    // @ts-ignore
    newEvent.extendedProps = {
      task_type,
      task_id: task.id,
      missions: 'missions' in task ? task.missions : undefined,
      missions_info: 'missions_info' in task ? task.missions_info : undefined,
      scenario: 'scenario' in task ? task.scenario : undefined,
      robot_type,
      robot_name,
      robot_sn,
      robot_id: robot.id,
      mission_name: mission_name || '',
      mission_id,
      vendor: vendor,
      facility_ids: facilityIds,
    }
    events.push(newEvent)
  }

  return events
}

export const addEvents = (
  start: Date,
  end: Date,
  period: number,
  id: string,
  title: string,
  getEventColor: () => string | undefined,
  duration: number,
  except_days: string[] = [],
  description: string | React.ReactNode,
  counter: number,
  extendedProps?: any,
  except_days_descriptions?: Record<string, string> | null,
  except_days_users?: Record<string, string> | null
): { events: ProcessedEvent[]; counter: number } => {
  let events: ProcessedEvent[] = []
  const today = new Date()
  while (start <= end) {
    const dateStr = toISOStringYYYYMMDD(start)
    if (except_days.includes(dateStr)) {
      if (except_days_descriptions && except_days_descriptions[dateStr]) {
        const desc = except_days_descriptions[dateStr]
        const user = except_days_users?.[dateStr]

        const fullDesc = React.createElement(
          'div',
          {},
          description,
          React.createElement(
            'div',
            { style: { marginTop: '8px', borderTop: '1px solid #ccc', paddingTop: '4px' } },
            React.createElement('div', { style: { fontWeight: 'bold' } }, 'The task was canceled'),
            React.createElement('span', { style: { fontWeight: 'bold' } }, 'Reason: '),
            desc,
            ...(user
              ? [
                  React.createElement('br', {}),
                  React.createElement('span', { style: { fontWeight: 'bold' } }, 'Canceled by: '),
                  user,
                ]
              : [])
          )
        )

        events.push({
          start: start,
          end: addSeconds(start, duration),
          event_id: id + '_' + counter,
          title: title,
          color: 'gray', // Use a distinct color for excluded events
          extendedProps: {
            ...extendedProps,
            status: 'deleted',
            exception_reason: desc,
            exception_user: user,
          },
          description: fullDesc,
          editable: false,
          deletable: false,
        })
        counter++
      }
      start = new Date(start.valueOf() + period)
      continue
    }
    let displayedDescription = description
    let editable = true
    if (start < today) {
      displayedDescription = React.createElement(React.Fragment, {}, t('scheduler.no_statistic') + ' \n', description)
      editable = false
    }
    events.push({
      start: start,
      end: addSeconds(start, duration),
      event_id: id + '_' + counter,
      title: title,
      color: getEventColor(),
      extendedProps: {
        ...extendedProps,
        status: 'scheduled',
      },
      description: displayedDescription,
      editable: editable,
      deletable: editable,
    })
    start = new Date(start.valueOf() + period)
    counter++
  }

  return { events, counter }
}

export const renderStatsTable = (eventProgerss: EventStatisticsProgress) => {
  if (eventProgerss) {
    const rows: { label: string; value: string }[] = []

    const efficiency =
      eventProgerss.cleaning_performance > 0.01
        ? eventProgerss.cleaning_performance
        : (eventProgerss.cleaned_area_actual / eventProgerss.duration_overall) * 3600
    if (efficiency) {
      rows.push({ label: '• Cleaning performance:', value: `${efficiency.toFixed(1)} m\u00B2/h` })
    }

    // Progress: prefer explicit completion_percentage (GS), fallback to travel_distance ratio (Autonomics).
    const completionRaw = (eventProgerss as any).completion_percentage
    if (completionRaw != null && Number.isFinite(Number(completionRaw))) {
      let pct = Number(completionRaw)
      // GS reports may store 0..1; normalize to percent.
      if (pct >= 0 && pct <= 1) pct = pct * 100
      // Clamp and display if meaningful.
      pct = Math.max(0, Math.min(100, pct))
      if (pct > 0) rows.push({ label: '• Progress:', value: `${pct.toFixed(1)}%` })
    } else {
      let mission_progress = (eventProgerss.travel_distance_actual / eventProgerss.travel_distance_planned) * 100.0
      mission_progress = mission_progress > 99.0 ? 100.0 : mission_progress
      if (mission_progress) {
        rows.push({ label: '• Progress:', value: `${mission_progress.toFixed(1)}%` })
      }
    }

    if (eventProgerss.cleaned_area_planned) {
      rows.push({ label: '• Cleaning area:', value: `${eventProgerss.cleaned_area_planned.toFixed(1)} m\u00B2` })
    }

    if (eventProgerss.duration_overall) {
      rows.push({ label: '• Duration:', value: secToTime(eventProgerss.duration_overall) })
    }

    if ((eventProgerss as any).battery_begin != null && (eventProgerss as any).battery_end != null) {
      const begin = Number((eventProgerss as any).battery_begin)
      const end = Number((eventProgerss as any).battery_end)
      const diff = begin - end
      if (Number.isFinite(diff) && diff !== 0) {
        if (diff > 0) rows.push({ label: '• Battery charge used:', value: `${diff.toFixed(1)}%` })
        else rows.push({ label: '• Battery charged:', value: `${Math.abs(diff).toFixed(1)}%` })
      }
    }

    if (eventProgerss.water_level_begin != null && eventProgerss.water_level_end != null) {
      const value = eventProgerss.water_level_begin - eventProgerss.water_level_end
      if (value > 0) rows.push({ label: '• Clean water used:', value: `${value.toFixed(1)}%` })
    } else if ((eventProgerss as any).water_consumption != null) {
      const value = Number((eventProgerss as any).water_consumption)
      if (Number.isFinite(value) && value > 0) {
        const unit = String((eventProgerss as any).water_consumption_unit || '%')
        rows.push({ label: '• Clean water used:', value: `${value.toFixed(1)}${unit === 'percent' ? '%' : ` ${unit}`}` })
      }
    }

    // Dirty water filled (normalized sources provide dirty_water_consumption, Autonomics progress may have levels).
    if ((eventProgerss as any).dirty_water_consumption != null) {
      const value = Number((eventProgerss as any).dirty_water_consumption)
      if (Number.isFinite(value) && value > 0) {
        const unit = String((eventProgerss as any).dirty_water_consumption_unit || '%')
        rows.push({ label: '• Dirty water filled:', value: `${value.toFixed(1)}${unit === 'percent' ? '%' : ` ${unit}`}` })
      }
    } else if (
      (eventProgerss as any).dirty_water_level_begin != null &&
      (eventProgerss as any).dirty_water_level_end != null
    ) {
      const begin = Number((eventProgerss as any).dirty_water_level_begin)
      const end = Number((eventProgerss as any).dirty_water_level_end)
      const value = end - begin
      if (Number.isFinite(value) && value > 0) rows.push({ label: '• Dirty water filled:', value: `${value.toFixed(1)}%` })
    }

    // Garbage filled (Autonomics progress uses garbage_filled_begin/end; normalized also has them).
    if ((eventProgerss as any).garbage_filled_begin != null && (eventProgerss as any).garbage_filled_end != null) {
      const begin = Number((eventProgerss as any).garbage_filled_begin)
      const end = Number((eventProgerss as any).garbage_filled_end)
      const value = end - begin
      if (Number.isFinite(value) && value > 0) rows.push({ label: '• Garbage filled:', value: `${value.toFixed(1)}%` })
    }

    if (rows.length === 0) return null

    return React.createElement(
      'table',
      { style: { width: 'auto', fontSize: '0.875rem', borderCollapse: 'collapse' } },
      React.createElement(
        'tbody',
        {},
        rows.map((row, index) =>
          React.createElement(
            'tr',
            { key: index },
            React.createElement(
              'td',
              { style: { paddingRight: '10px', verticalAlign: 'top', whiteSpace: 'nowrap' } },
              row.label
            ),
            React.createElement('td', { style: { verticalAlign: 'top' } }, row.value)
          )
        )
      )
    )
  }
  return null
}

const isTaskStat = (s: EventStatistics | OdysseyEventStatistics): s is EventStatistics => {
  return 'task_id' in s
}

export const applyStatToEvent = (event: ProcessedEvent, stat: EventStatistics | OdysseyEventStatistics) => {
  const isTask = isTaskStat(stat)
  const status = isTask ? (stat as EventStatistics).mission_status : (stat as OdysseyEventStatistics).status
  event.title =
    status === 4
      ? '✔ ' + event.title
      : status === 255
      ? '？ ' + event.title
      : status === 7
      ? '⛔ ' + event.title
      : status === 8
      ? '⚠️ ' + event.title
      : status === 99
      ? '' + event.title
      : '⏳ ' + event.title

  if (isTaskStat(stat)) {
    event.extendedProps = {
      ...event.extendedProps,
      status: 'executed',
      mission_status: status,
      mission_progress: stat.mission_progress,
    }
    const description = renderStatsTable(stat.mission_progress)
    const reportDescription = React.createElement(
      'div',
      { style: { marginTop: '4px' } },
      description
        ? React.createElement(
            'div',
            {},
            React.createElement('div', { style: { fontWeight: 'bold' } }, 'Report:'),
            description
          )
        : null
    )
    if (typeof event.description === 'string' && event.description === '') {
      event.description = reportDescription
    } else {
      event.description = React.createElement(React.Fragment, {}, event.description, reportDescription)
    }
    event.start = new Date(stat.created_at.replace(' ', 'T'))
    event.end = addSeconds(
      event.start,
      stat.mission_progress.duration_overall > 0.1 ? stat.mission_progress.duration_overall : 10
    )
  } else {
    event.extendedProps = {
      ...event.extendedProps,
      status: 'executed',
      overall_progress: (stat as OdysseyEventStatistics).overall_progress,
      missions_progress: (stat as OdysseyEventStatistics).missions_progress,
    }
    event.start = new Date((stat as OdysseyEventStatistics).created_at.replace(' ', 'T'))
    event.end = addSeconds(
      event.start,
      (stat as OdysseyEventStatistics).overall_progress.duration_overall > 0.1
        ? (stat as OdysseyEventStatistics).overall_progress.duration_overall
        : 10
    )
  }
}

export const renderMissionsWithNames = (missions: { id: number; name: string }[]) => {
  if (missions && missions.length === 1) {
    const m = missions[0]
    return React.createElement(
      'div',
      { style: { marginTop: '0px', fontSize: '0.875rem' } },
      React.createElement('span', { style: { fontWeight: 'bold' } }, 'Mission: '),
      React.createElement(
        'a',
        {
          className: schedulerStyle.link,
          href: `${PATH.MISSION}${m.id}`,
          target: '_blank',
          rel: 'noreferrer',
        },
        m.id.toString()
      ),
      ` ${m.name}`
    )
  }

  if (missions && missions.length > 1) {
    const label = React.createElement('div', { style: { marginBottom: '0px', fontWeight: 'bold' } }, 'Missions:')

    const tableHeader = React.createElement(
      'thead',
      {},
      React.createElement(
        'tr',
        {},
        React.createElement('th', { style: { textAlign: 'left', paddingRight: '10px' } }, '#'),
        React.createElement('th', { style: { textAlign: 'left', paddingRight: '10px' } }, 'ID'),
        React.createElement('th', { style: { textAlign: 'left' } }, 'Name')
      )
    )

    const tableBody = React.createElement(
      'tbody',
      {},
      missions.map((m, index) =>
        React.createElement(
          'tr',
          { key: `${m.id}_${index}` },
          React.createElement('td', { style: { paddingRight: '10px' } }, `${index + 1}`),
          React.createElement(
            'td',
            { style: { paddingRight: '10px' } },
            React.createElement(
              'a',
              {
                className: schedulerStyle.link,
                href: `${PATH.MISSION}${m.id}`,
                target: '_blank',
                rel: 'noreferrer',
              },
              m.id.toString()
            )
          ),
          React.createElement('td', {}, m.name)
        )
      )
    )

    return React.createElement(
      'div',
      { style: { marginTop: '0px', fontSize: '0.875rem' } },
      label,
      React.createElement('table', { style: { width: 'auto' } }, tableHeader, tableBody)
    )
  }
  return null
}

export const renderMissionName = (name: string) => {
  return React.createElement(
    'div',
    { style: { marginBottom: '0px' } },
    React.createElement('span', { style: { fontWeight: 'bold' } }, 'Mission name:'),
    ` ${name}`
  )
}

export const renderScenario = (scenario: number) => {
  return React.createElement(
    'div',
    { style: { marginBottom: '2px' } },
    React.createElement('span', { style: { fontWeight: 'bold' } }, 'Scenario: '),
    React.createElement(
      'a',
      {
        className: schedulerStyle.link,
        href: `${PATH.SCENARIOS}${scenario}`,
        target: '_blank',
        rel: 'noreferrer',
      },
      scenario.toString()
    )
  )
}

export const renderMissions = (missions: (number | { id: number; name: string })[]) => {
  const validMissions = missions.filter(m => m != null && m !== 0)
  if (validMissions.length > 0) {
    const children: React.ReactNode[] = []
    children.push(
      React.createElement(
        'span',
        { style: { fontWeight: 'bold' } },
        validMissions.length > 1 ? 'Missions: ' : 'Mission: '
      )
    )
    validMissions.forEach((m: any, index: number) => {
      const id = typeof m === 'object' ? m.id : m
      const name = typeof m === 'object' ? m.name : m.toString()

      children.push(
        React.createElement(
          React.Fragment,
          { key: `${id}_${index}` },
          React.createElement(
            'a',
            {
              className: schedulerStyle.link,
              href: `${PATH.MISSION}${id}`,
              target: '_blank',
              rel: 'noreferrer',
            },
            name
          ),
          index < validMissions.length - 1 ? ', ' : ''
        )
      )
    })
    children.push(React.createElement('br', {}))
    return React.createElement(
      'div',
      { className: schedulerStyle.missionsList, style: { marginTop: '0px' } },
      ...children
    )
  }
  return null
}

export const addEventsStatistics = (
  events: ProcessedEvent[],
  events_statistics: (EventStatistics | OdysseyEventStatistics)[],
  robots: RobotRead[],
  missionMap: Map<number, string>,
  robotIdToFacilityIds: Map<number, number[]>
) => {
  // Create a map of robot serial numbers to robot objects for efficient lookup
  const robotMap = new Map<string, RobotRead>()
  robots.forEach(robot => {
    robotMap.set(robot.serial_number, robot)
  })

  events_statistics.forEach(stat => {
    const isTask = isTaskStat(stat)
    const id = isTask ? stat.task_id : (stat as OdysseyEventStatistics).odyssey_id
    const missions = isTask
      ? [stat.mission_id]
      : (stat as OdysseyEventStatistics).overall_progress.missions
      ? (stat as OdysseyEventStatistics).overall_progress.missions
      : (stat as OdysseyEventStatistics).missions_progress.map(m => m.id)

    const prefix = isTask ? 'task_' : 'odyssey_'
    const eventIdStr = prefix + id
    const statDateStr = toISOStringYYYYMMDD(new Date(stat.created_at))

    const eventIndex = events.findIndex(e => {
      const eId = String(e.event_id)
      const parts = eId.split('_')
      if (parts[0] !== (isTask ? 'task' : 'odyssey')) return false
      if (Number(parts[1]) !== id) return false
      if (toISOStringYYYYMMDD(e.start) !== statDateStr) return false
      // extra check for events with icons
      const titleStartsWithIcon = ['✔', '？', '⛔', '⚠️', '⏳'].some(icon => e.title.startsWith(icon + ' '))
      return !titleStartsWithIcon
    })

    if (eventIndex !== -1) {
      events[eventIndex].editable = false
      events[eventIndex].deletable = false
      events[eventIndex].draggable = false
      applyStatToEvent(events[eventIndex], stat)
    } else {
      const duration = isTask
        ? (stat as EventStatistics).mission_progress.duration_overall
        : (stat as OdysseyEventStatistics).overall_progress.duration_overall
      const task_name =
        isTask || (stat as OdysseyEventStatistics).overall_progress.name === undefined
          ? ''
          : ' | ' + (stat as OdysseyEventStatistics).overall_progress.name
      const taskName = 'Robot ' + (stat as any).robot_sn + ' | ' + 'Task  ' + id + task_name
      const description = React.createElement(
        React.Fragment,
        {},
        (stat as any).scenario_id ? renderScenario((stat as any).scenario_id) : null,
        (stat as any).status === 99
          ? React.createElement(
              React.Fragment,
              {},
              t('scheduler.no_statistic') + '\n',
              renderMissionsWithNames((stat as OdysseyEventStatistics).missions_info)
            )
          : ''
      )
      const robot = robotMap.get((stat as any).robot_sn.toString())
      const robotTypeEffective = robot != null ? (robot as any).robot_type_display || robot.robot_type : undefined
      const facilityIds = robot?.id != null ? robotIdToFacilityIds.get(robot.id) ?? [] : []
      const vendor = ((robot as any)?.vendor as VendorCode | undefined) || VENDOR.AUTONOMICS
      const newEvent: ProcessedEvent = {
        event_id: eventIdStr + '_stat_' + stat.id,
        title: taskName,
        start: new Date((stat as any).created_at.replace(' ', 'T')),
        end: addSeconds(new Date((stat as any).created_at.replace(' ', 'T')), duration > 0.1 ? duration : 10),
        color: getColorByColorName(robot?.robot_color || 'White'),
        description: description,
        extendedProps: {
          task_type: isTask ? 'Task' : 'Odyssey',
          task_id: id,
          missions: missions,
          missions_info: (stat as OdysseyEventStatistics).missions_info || [],
          robot_type: robotTypeEffective ?? robot?.robot_type,
          robot_name: robot?.name,
          robot_sn: (stat as any).robot_sn,
          robot_id: robot?.id,
          mission_name: isTask
            ? (stat as EventStatistics).mission_progress.name
            : (stat as OdysseyEventStatistics).overall_progress.name,
          vendor: vendor,
          facility_ids: facilityIds,
        },
        editable: false,
        deletable: false,
        draggable: false,
      }

      applyStatToEvent(newEvent, stat)
      events.push(newEvent)
    }
  })
}

const getEventStats = (
  task_id: number,
  date: Date,
  stats: (EventStatistics | OdysseyEventStatistics)[]
): EventStatistics | OdysseyEventStatistics | null => {
  const eventStats = stats.find(stat => {
    const createdMatch = toISOStringYYYYMMDD(new Date(stat.created_at)) === toISOStringYYYYMMDD(date)
    if (isTaskStat(stat)) {
      return stat.task_id === task_id && createdMatch
    } else {
      return (stat as OdysseyEventStatistics).odyssey_id === task_id && createdMatch
    }
  })

  if (eventStats) {
    return eventStats
  }
  return null
}

const idToColorMap: Record<number, string> = {
  1: 'BlueViolet',
  2: 'Crimson',
  3: 'DarkCyan',
  4: 'DodgerBlue',
  5: 'ForestGreen',
  6: 'Gold',
  7: 'Indigo',
  8: 'LimeGreen',
  9: 'MediumOrchid',
  10: 'MediumPurple',
  11: 'MediumSeaGreen',
  12: 'NavajoWhite',
  13: 'OliveDrab',
  14: 'OrangeRed',
  15: 'Orchid',
  16: 'PaleTurquoise',
  17: 'RoyalBlue',
  18: 'SlateBlue',
  19: 'SpringGreen',
  20: 'Tomato',
  21: 'Turquoise',
  22: 'Violet',
  23: 'Wheat',
  24: 'YellowGreen',
  25: 'Aqua',
  26: 'Aquamarine',
  27: 'CornflowerBlue',
  28: 'HotPink',
  29: 'Chartreuse',
  30: 'Chocolate',
  31: 'CadetBlue',
  32: 'DarkGoldenRod',
  33: 'DarkKhaki',
  34: 'DarkMagenta',
  35: 'DarkOliveGreen',
  36: 'DarkOrange',
  37: 'DarkOrchid',
  38: 'DarkRed',
  39: 'DarkSalmon',
  40: 'DarkSlateBlue',
  41: 'DeepPink',
  42: 'DeepSkyBlue',
  43: 'DimGray',
  44: 'FireBrick',
  45: 'Gainsboro',
  46: 'GhostWhite',
  47: 'GoldenRod',
  48: 'GreenYellow',
  49: 'BurlyWood',
  50: 'IndianRed',
  51: 'Ivory',
  52: 'Khaki',
  53: 'Lavender',
  54: 'LavenderBlush',
  55: 'LawnGreen',
  56: 'LightCoral',
  57: 'LightCyan',
  58: 'LightGoldenRodYellow',
  59: 'LightPink',
  60: 'LightSalmon',
}
const nameToColorMap: Record<string, string> = {
  White: 'DarkGrey',
  Black: 'Black',
  Pink: 'HotPink',
  Red: 'FireBrick',
  Blue: 'CornflowerBlue',
  Green: 'Green',
  Yellow: 'Gold',
  Orange: 'DarkOrange',
  Purple: 'RebeccaPurple',
}

export const getColorById = (id: number): string => {
  return idToColorMap[id] || 'Gray' // Default color
}

export const getColorByColorName = (color: string): string => {
  return nameToColorMap[color] || 'Gray' // Default color
}

export function secToTime(sec: number): string {
  const totalSeconds = Math.max(0, Number(sec) || 0)
  const totalHours = Math.floor(totalSeconds / 3600)
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.round(totalSeconds % 60)

  const parts: string[] = []
  if (days > 0) parts.push(days + 'd')
  if (hours > 0 || days > 0) parts.push(hours + 'h')
  if (minutes > 0 || hours > 0 || days > 0) parts.push(minutes + 'm')
  parts.push(seconds + 's')
  return parts.join(' ')
}

// Pad a number to 2 digits
function pad(n: number): string {
  return `${Math.floor(Math.abs(n))}`.padStart(2, '0')
}

// Get timezone offset in ISO format (+hh:mm or -hh:mm)
function getTimezoneOffset(date: Date): string {
  const tzOffset = -date.getTimezoneOffset()
  const diff = tzOffset >= 0 ? '+' : '-'
  return diff + pad(tzOffset / 60) + ':' + pad(tzOffset % 60)
}

// Convert Date to local time zone ISO string that contains timezone information
export function toISOStringWithTimezone(date: Date): string {
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    getTimezoneOffset(date)
  )
}

// Convert Date to local time zone ISO string YYYY-MM-DD
export function toISOStringYYYYMMDD(date: Date): string {
  return '' + date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ''
}

export const getTaskId = (eventId: string): number => {
  const [, rawId] = eventId.split('_')
  return Number(rawId)
}

export const getTaskPrefix = (eventId: string): string => {
  const [prefix] = eventId.split('_')
  return prefix
}

export const getLoadingStatus = (status?: string): string => {
  if (!status) return ''
  return status === 'new' || status === 'scenario_error'
    ? 'The task has not been loaded to the robot'
    : status === 'scenario_done'
    ? 'The task has been loaded to the robot'
    : 'The task is loading to the robot'
}
